/**
 * i18n messages for the "Send for Signatures" dialog (en/ar). Registered in
 * core/plugins/i18n.ts under its own top-level `sendSignatures` key: that merge
 * is shallow, so the namespace must not be shared with another file.
 * CSP-safe compiler: only `{named}` placeholders.
 */
export const sendSignaturesMessages = {
  en: {
    sendSignatures: {
      titleCertificate: "Send Certificate for Signatures",
      titleSubCertificate: "Send Sub-Certificate for Signatures",
      checking: "Checking scholar signatures...",
      scholarsCertificate: "Scholars on this Certificate:",
      scholarsSubCertificate: "Scholars on this Sub-Certificate:",
      noEmail: "No email",
      chip: {
        signedStored: "Signed (stored)",
        signedDigital: "Signed (digital)",
        signedLegacy: "Signed (earlier signature)",
        consent: "Consent on file",
        noConsent: "No consent",
        noStored: "No stored signature",
      },
      options: "Signature Options:",
      applyAll: "Apply stored signatures (all scholars)",
      applyAllHint: "Each scholar needs a consent and a stored signature.",
      partial: "Stored where consented, send to the others",
      partialCount: "({count} scholar(s) will receive email)",
      sendAll: "Send to all scholars for digital signature",
      available: "Available",
      standardEmailNote:
        "Scholars receive the standard email in English and Arabic.",
      notify: "Email the certificate to its scholars",
      cancel: "Cancel",
      applyButton: "Apply stored signatures",
      sendButton: "Send for Signatures",
      appliedNote:
        "The stored signatures were applied to {count} scholar(s) before this failed.",
      preparing: "Preparing for signatures...",
      sentOk: "Sent for signatures successfully!",
      emailsMaybeFailed:
        "Prepared for signatures, but some emails may not have been sent. Please check the Signature Platform for status.",
      notLoggedIn: "User not logged in. Please log in and try again.",
      sendFailed: "Failed to send for signatures. Please try again.",
      checkFailed: "Failed to check scholar signatures. Please try again.",
      applyFailed: "Could not apply the stored signatures.",
      notifying: "Emailing the certificate to its scholars...",
      // Plural categories as Intl.PluralRules names them (the CSP-safe
      // message compiler has no plural syntax); `other` is the fallback.
      notifiedCount: {
        one: "Sent to {count} scholar",
        other: "Sent to {count} scholars",
      },
      skippedCount: {
        one: "{count} without an email address",
        other: "{count} without an email address",
      },
      failedCount: {
        one: "{count} could not be sent",
        other: "{count} could not be sent",
      },
      notifyFailed:
        "The signatures were applied, but the certificate could not be emailed.",
    },
  },
  ar: {
    sendSignatures: {
      titleCertificate: "إرسال الشهادة للتوقيعات",
      titleSubCertificate: "إرسال الشهادة الفرعية للتوقيعات",
      checking: "جارٍ التحقق من توقيعات العلماء...",
      scholarsCertificate: "العلماء في هذه الشهادة:",
      scholarsSubCertificate: "العلماء في هذه الشهادة الفرعية:",
      noEmail: "لا يوجد بريد إلكتروني",
      chip: {
        signedStored: "موقّعة (محفوظ)",
        signedDigital: "موقّعة (رقمي)",
        signedLegacy: "موقّعة (توقيع سابق)",
        consent: "الموافقة محفوظة",
        noConsent: "لا توجد موافقة",
        noStored: "لا يوجد توقيع محفوظ",
      },
      options: "خيارات التوقيع:",
      applyAll: "تطبيق التواقيع المحفوظة (جميع العلماء)",
      applyAllHint: "يحتاج كل عالم إلى موافقة وتوقيع محفوظ.",
      partial: "المحفوظة حيث توجد موافقة، والإرسال للبقية",
      partialCount: "({count} عالم سيستلمون بريدًا إلكترونيًا)",
      sendAll: "إرسال إلى جميع العلماء للتوقيع الرقمي",
      available: "متاح",
      standardEmailNote:
        "يتلقى العلماء الرسالة المعتمدة باللغتين الإنجليزية والعربية.",
      notify: "إرسال الشهادة بالبريد الإلكتروني إلى علمائها",
      cancel: "إلغاء",
      applyButton: "تطبيق التواقيع المحفوظة",
      sendButton: "إرسال للتوقيعات",
      appliedNote:
        "تم تطبيق التواقيع المحفوظة على {count} عالم قبل حدوث الفشل.",
      preparing: "جارٍ التحضير للتوقيعات...",
      sentOk: "تم الإرسال للتوقيعات بنجاح!",
      emailsMaybeFailed:
        "تم التحضير للتوقيعات، لكن قد لا تكون بعض رسائل البريد قد أُرسلت. يرجى مراجعة منصة التوقيع.",
      notLoggedIn:
        "المستخدم غير مسجّل الدخول. يرجى تسجيل الدخول والمحاولة مرة أخرى.",
      sendFailed: "تعذّر الإرسال للتوقيعات. حاول مرة أخرى.",
      checkFailed: "تعذّر التحقق من توقيعات العلماء. حاول مرة أخرى.",
      applyFailed: "تعذّر تطبيق التواقيع المحفوظة.",
      notifying: "جارٍ إرسال الشهادة بالبريد الإلكتروني إلى علمائها...",
      notifiedCount: {
        zero: "لم تُرسل إلى أي عالم",
        one: "أُرسلت إلى عالم واحد",
        two: "أُرسلت إلى عالمَين",
        few: "أُرسلت إلى {count} علماء",
        many: "أُرسلت إلى {count} عالمًا",
        other: "أُرسلت إلى {count} عالم",
      },
      skippedCount: {
        zero: "لا يوجد عالم بلا بريد إلكتروني",
        one: "عالم واحد بلا بريد إلكتروني",
        two: "عالمان بلا بريد إلكتروني",
        few: "{count} علماء بلا بريد إلكتروني",
        many: "{count} عالمًا بلا بريد إلكتروني",
        other: "{count} عالم بلا بريد إلكتروني",
      },
      failedCount: {
        zero: "لم يتعذّر الإرسال إلى أي عالم",
        one: "تعذّر الإرسال إلى عالم واحد",
        two: "تعذّر الإرسال إلى عالمَين",
        few: "تعذّر الإرسال إلى {count} علماء",
        many: "تعذّر الإرسال إلى {count} عالمًا",
        other: "تعذّر الإرسال إلى {count} عالم",
      },
      notifyFailed:
        "تم تطبيق التواقيع، لكن تعذّر إرسال الشهادة بالبريد الإلكتروني.",
    },
  },
};
